import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { loginSuccess } from "../store/slices/authSlice";
import api from "../services/api";

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await api.login(username, password);
      dispatch(
        loginSuccess({
          user: response.user,
          token: response.token,
        })
      );
      navigate("/");
    } catch (err: any) {
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundImage: "url('/background.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(25, 75, 255, 0.1)",
          zIndex: 1,
        },
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          m: 2,
          position: "relative",
          zIndex: 2,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          color: "#1e293b",
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                backgroundColor: "white",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                mb: 2,
              }}
            >
              <img
                src="/logo.svg"
                alt="Vangard Pay"
                style={{ width: "60px", height: "auto", maxHeight: "60px" }}
              />
            </Box>
          </Box>
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ color: "#1e293b" }}>
            Vangard Pay
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 3, color: "#64748b" }}
          >
            Testing Interface
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              autoFocus
              sx={{
                "& .MuiInputBase-input": { color: "#1e293b" },
                "& .MuiInputLabel-root": { color: "#64748b" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#cbd5e1" },
                  "&:hover fieldset": { borderColor: "#94a3b8" },
                },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              sx={{
                "& .MuiInputBase-input": { color: "#1e293b" },
                "& .MuiInputLabel-root": { color: "#64748b" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#cbd5e1" },
                  "&:hover fieldset": { borderColor: "#94a3b8" },
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, minHeight: 48, position: 'relative' }}
              disabled={loading}
            >
              {loading && (
                <CircularProgress
                  size={20}
                  sx={{
                    color: 'inherit',
                    position: 'absolute',
                    left: 24,
                    top: '50%',
                    marginTop: '-10px',
                  }}
                />
              )}
              Login
            </Button>
          </form>

        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
